import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { useEmbeddedMode } from "@src/appV2/EmbeddedMode/EmbeddedModeContext";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { StickyScrollPageHeader } from "@src/appV2/redesign/components/StickyScrollPageHeader";
import { useHistory, useLocation } from "react-router-dom";

import { type PlacementWithDetails } from "../../../api/useFetchPaginatedPlacements";
import { getHighestRankingQualification } from "../../../api/useHiringQualificationRanking";
import { PLACEMENT_PATHS } from "../../../paths";
import { useSharePlacementJob } from "../../PlacementShareJob/useSharePlacementJob";

interface PlacementDetailsPageHeaderProps {
  placement: PlacementWithDetails;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  onFavoritePlacementChange: () => Promise<void>;
  isUpdatingFavoritedStatus: boolean;
}

export function PlacementDetailsPageHeader(props: PlacementDetailsPageHeaderProps) {
  const { placement, scrollContainerRef, isUpdatingFavoritedStatus, onFavoritePlacementChange } =
    props;
  const history = useHistory();
  const location = useLocation();
  const { isEmbedded } = useEmbeddedMode();
  const { handleShareJob, isSharing } = useSharePlacementJob();

  function handleBackClick() {
    // Only use browser history when all three conditions are met:
    // 1. Not in embedded mode (embedded history includes auth/external pages that would exit the iframe)
    // 2. Location key exists (confirms we navigated here via the router, not a manual URL entry)
    // 3. Meaningful history depth (history.length <= 2 means fresh deep link with nowhere useful to go back to)
    // Otherwise, navigate to the Jobs feed as a safe default.
    if (!isEmbedded && isDefined(location.key) && history.length > 2) {
      history.goBack();
    } else {
      history.replace(PLACEMENT_PATHS.ROOT);
    }
  }

  const { title, isFavorited } = placement;
  const qualificationDisplayLabel = getHighestRankingQualification(placement.workerTypes ?? []);

  return (
    <StickyScrollPageHeader
      shouldWrapTitle
      headerVariant="tertiary"
      title={title}
      scrollContainerRef={scrollContainerRef}
      rightAction={
        <Stack direction="row" spacing={2}>
          <IconButton
            disabled={isSharing}
            iconType="share"
            variant="outlined"
            onClick={async () => {
              await handleShareJob({
                placementId: placement.id,
                payRange: placement.payRange,
                workplaceName: placement.workplace.name,
                qualificationDisplayLabel: qualificationDisplayLabel ?? "",
                formattedAddress: placement.address.formatted ?? "",
              });
            }}
          />
          <IconButton
            disabled={isUpdatingFavoritedStatus}
            iconType={isFavorited ? "like-filled" : "like-outlined"}
            variant="outlined"
            onClick={onFavoritePlacementChange}
          />
        </Stack>
      }
      onClickBack={() => {
        handleBackClick();
      }}
    />
  );
}
